import { getMaxDigitWithDot } from '@trader/utils';
import { TPlaceOrderSide } from '@trader/types';

import { TVariation } from '../types';
import { calculateProfitPips, calculateStopLossPips } from './pipsCalculating';

const hundred = 100;

export const handleCheckRangeValue = (
  value: string,
  minValue: number,
  maxValue: number,
  increment: number,
  fixDigit: number,
  onTranslateErrors,
  isAmount?: boolean
): string => {
  const convertedValue = value ? Number(Number(value).toFixed(fixDigit)) : 0;

  const isNotDecimal = convertedValue % 1 === 0;
  const isNotMultiple = convertedValue
    ? isNotDecimal && (convertedValue * hundred) % (increment * hundred) !== 0
    : false;
  if (isAmount && isNotMultiple) {
    return onTranslateErrors(
      'COMMON.ERRORS.SHOULD_BE_MULTIPLE_OF_THE_MINIMUM_LOTS',
      {
        amount: increment.toFixed(fixDigit),
      }
    );
  }

  if (convertedValue < minValue) {
    return onTranslateErrors('COMMON.ERRORS.MUST_BE_HIGHER_THAN', {
      amount: minValue.toFixed(fixDigit),
    });
  }

  if (convertedValue > maxValue) {
    return onTranslateErrors('COMMON.ERRORS.MUST_BE_LOWER_THAN', {
      amount: maxValue.toFixed(fixDigit),
    });
  }

  return '';
};

const fixToNineDigit = 9;

export const getMarketValueVolume = (
  side: TPlaceOrderSide,
  ask: number,
  bid: number,
  amount: number,
  maxDigit: number
) => {
  const result =
    side === 'Buy'
      ? Number(
          (Number(amount) * Number(bid)).toString().slice(0, fixToNineDigit)
        ).toFixed(getMaxDigitWithDot(maxDigit))
      : Number(
          (Number(amount) * Number(ask)).toString().slice(0, fixToNineDigit)
        ).toFixed(getMaxDigitWithDot(maxDigit));

  return Number(result);
};

interface IGetProtectionValue {
  loss: string;
  profit: string;
}

export const getProtectionValue = (
  side: TPlaceOrderSide,
  ask: string,
  bid: string,
  fixDigit: number,
  step: number,
  stopsLevel: number
): IGetProtectionValue => {
  return {
    loss: calculateStopLossPips({
      ask,
      bid,
      side,
      pipSize: step,
      stopsLevel,
      fixDigit,
    }),
    profit: calculateProfitPips({
      ask,
      bid,
      side,
      pipSize: step,
      stopsLevel,
      fixDigit,
    }),
  };
};

export const getMaxDigitValueForAmount = (value: number): number => {
  if (!value) {
    return 0;
  }
  if (value.toString().includes('.')) {
    const maxDigit = value.toString().split('.');
    const valueMax = maxDigit[maxDigit.length - 1];
    return valueMax.length;
  }
  return 0;
};

interface IGetProtectionValueRange {
  min: number;
  max: number;
}

export const getProtectionValueRange = (
  side: TPlaceOrderSide,
  ask: string,
  priceIncrement: number,
  bid: string
): IGetProtectionValueRange => ({
  min: side === 'Sell' ? Number(ask) : priceIncrement,
  max: side === 'Sell' ? Infinity : Number(bid),
});

export const getMarketReq = (
  ask: string,
  bid: string,
  leverage: number,
  volume: number
): number => {
  const defaultLeverage = 1;
  const halfPrice = 2;
  const value =
    ((Number(ask) + Number(bid)) / halfPrice) *
      (volume / (leverage || defaultLeverage)) +
    volume * (Number(ask) - Number(bid));
  return Number(value) < 0 ? 0 : Number(value);
};

export const getAmount = (
  value: string,
  fixDigit: number,
  variation: TVariation,
  step: number
): string => {
  const amount =
    variation === 'minus' ? Number(value) - step : Number(value) + step;
  return amount.toFixed(fixDigit);
};
